import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled from 'styled-components';

import { Coupon } from '@services/product/types/coupon';

import Box from '@components/layout/box';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import SeparatorLine from '@components/basics/separator-line';

import { Order } from '@webapp/types/orders/order';
import { Translate } from 'react-localize-redux';
import moment from 'moment';
import { PRO_TRIAL_DAYS } from '@services/subscription/constants';

interface OrderCtrlProps {
  className?: string;
  basket: Order[];
  children?: React.ReactNode;
  title?: React.ReactNode;
  recurrent?: boolean;
  activeCoupon?: Coupon;
  isMobile: boolean;
  isMerch?: boolean;
  isMerchPlan?: boolean;
  tax?: number | string;
  shippingFee?: number | string;
  productCost?: number | string;
  total?: number | string;
  trialDays?: number;
}

const mapStateToProps = (state: AppState) => ({
  isMobile: state.ui.mobile,
});

const getTotal = (basket: Order[]): string => {
  const amount = basket.reduce((total, order) => {
    return total + (order.price.value * order.quantity) / 100;
  }, 0);

  return amount.toFixed(2);
};

const getFinalPrice = (basket: Order[], activeCoupon?: Coupon) => {
  const amount = basket.reduce((total, order) => {
    const discountRatio = activeCoupon ? 1 - activeCoupon.percent_off / 100 : 1;
    const value = discountRatio * ((order.price.value * order.quantity) / 100);
    return total + value;
  }, 0);

  return amount.toFixed(2);
};

const getDiscountAmount = (basket: Order[], activeCoupon?: Coupon) => {
  const amount = basket.reduce((total, order) => {
    const discountRatio = activeCoupon ? activeCoupon.percent_off / 100 : 1;
    const value = discountRatio * ((order.price.value * order.quantity) / 100);
    return total + value;
  }, 0);

  return amount.toFixed(2);
};

const getDiscountDuration = (basket: Order[], coupon: Coupon) => {
  const isYearly = basket.some((order) => order.price.frequency === 'year');

  const monthDuration = `${coupon.duration_in_months} month`;
  const yearDuration = `${coupon.duration_in_months ? 'First year' : ''}`;

  return `${isYearly ? yearDuration : monthDuration} ${
    coupon.percent_off
  }% off`.trim();
};

const hasDiscount = (basket: Order[], activeCoupon?: Coupon) => {
  const value = parseFloat(getTotal(basket));

  if (value && !!activeCoupon) {
    return true;
  }

  return false;
};

const getDate = (trialDays?: number) => {
  if (trialDays) {
    return moment().add(trialDays, 'days').format('L');
  }
  return moment().format('L');
};

const getProductsFeatures = (basket: Order) => {
  if (basket.product) {
    return basket.product.features;
  }
  return;
};

export const OrderCtrl: React.FC<OrderCtrlProps> = (props) => {
  return (
    <BoxWrapper
      className={`${props.className}`}
      noBorderTop={props.isMobile}
      small
      collapse
    >
      <Box small noBorderBottom>
        {props.trialDays ? (
          <Flex
            className="mb-15"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography weight="700">
              <Translate
                id={
                  props.trialDays < PRO_TRIAL_DAYS
                    ? 'PAYMENT.FREE_TRIAL.CONTINUE'
                    : 'PAYMENT.FREE_TRIAL.TEXT'
                }
                data={{ daysLeft: props.trialDays }}
              />
            </Typography>
          </Flex>
        ) : null}

        <GridWrapper>
          {props.basket.map((order, i) => (
            <React.Fragment key={i}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                margin="4px 0"
                gap={8}
              >
                <Typography weight="700" type="small">
                  {props.title ? props.title : order.product.description}
                </Typography>
                <Typography weight="700" type="small">
                  ${getTotal([order])}
                </Typography>
              </Flex>
              <ItemsList>
                {getProductsFeatures(order)?.map((item, i) => (
                  <li key={`${item}-${i}`}>
                    <Typography type="small" weight="400">
                      {item}
                    </Typography>
                  </li>
                ))}
              </ItemsList>
            </React.Fragment>
          ))}
        </GridWrapper>

        {props.isMerch && (
          <>
            <Flex justifyContent="space-between" margin="4px 0">
              <Typography weight="400">
                <Translate id="MERCH.PRODUCT_SAMPLE.TITLE" />
              </Typography>
              <Typography weight="400">
                $
                {typeof props.productCost === 'string'
                  ? props.productCost
                  : props.productCost?.toFixed(2)}
              </Typography>
            </Flex>

            {!!props.shippingFee && (
              <Flex justifyContent="space-between" margin="4px 0">
                <Typography weight="400">
                  <Translate id="MERCH.PRODUCT_SAMPLE.SHIPPING.FEE" />
                </Typography>
                <Typography weight="400">${props.shippingFee}</Typography>
              </Flex>
            )}

            {!!props.tax && (
              <Flex justifyContent="space-between" margin="4px 0">
                <Typography weight="400">
                  <Translate id="MERCH.PRODUCT_SAMPLE.SALES.TAX" />
                </Typography>
                <Typography weight="400">${props.tax}</Typography>
              </Flex>
            )}
          </>
        )}

        {hasDiscount(props.basket, props.activeCoupon) ? (
          <>
            <SeparatorLine lineDot />
            <Flex justifyContent="space-between" alignItems="center">
              <Typography>
                {getDiscountDuration(
                  props.basket,
                  props.activeCoupon as Coupon
                )}
              </Typography>
              <DiscountAmount weight="400">
                ${getDiscountAmount(props.basket, props.activeCoupon)}
              </DiscountAmount>
            </Flex>
          </>
        ) : null}
      </Box>
      <SeparatorLine lineDot noMargin />
      <Box small>
        <Flex justifyContent="space-between" alignItems="center">
          <Typography weight="700">
            <Translate id="PAYMENT.TOTAL.TITLE" />
          </Typography>
          <Typography weight="700">
            $
            {props.total
              ? props.total
              : props.trialDays && props?.trialDays > 0
              ? '0.00'
              : getFinalPrice(props.basket, props.activeCoupon)}
          </Typography>
        </Flex>

        {props.recurrent ? (
          <ChargingDate type="small" opacity=".5" weight="400">
            <Translate
              id="PAYMENT.CHARGE_DATE.TEXT"
              data={{ date: getDate(props.trialDays) }}
            />
          </ChargingDate>
        ) : null}
      </Box>
      {props.children}
    </BoxWrapper>
  );
};

export default connect(mapStateToProps)(OrderCtrl);

const BoxWrapper = styled(Box)`
  position: relative;
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    max-width: 265px;
  }
`;

const DiscountAmount = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
`;

const ChargingDate = styled(Typography)`
  ${({ theme }) => `@media screen and ${theme.device.laptopS}`} {
    margin-top: 10px;
  }
`;

const GridWrapper = styled.div`
  display: grid;
  grid-gap: 20px;
`;

const ItemsList = styled.ul`
  margin: 0;
  padding: 0;
  padding-left: 14px;
  display: grid;
  grid-gap: 4px;
`;
